// packages
import axios from "axios";
import _ from "lodash";
import { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import {
  MdOutlineForwardToInbox,
  MdOutlineRemoveShoppingCart,
} from "react-icons/md";
import { PiSpinnerLight } from "react-icons/pi";
import { useNavigate } from "react-router-dom";

// components
import Header from "../components/Header";
import StandardContainer from "../components/StandardContainer";
import Alert from "../shared/components/Alert";
import ItemGrid from "../shared/components/ItemGrid";

// utils
import {
  clearQuoteItems,
  findQuoteItem,
  loadQuoteItems,
  toggleQuoteItem,
} from "../shared/utils/quote";
import { isCompanyUser } from "../shared/utils/user";

export default function QuoteCart() {
  const navigate = useNavigate();

  // data
  const [alertMessage, setAlertMessage] = useState("");
  const [alertVisible, setAlertVisible] = useState(false);
  const [companyId, setCompanyId] = useState("");
  const [customerNotes, setCustomerNotes] = useState("");
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [onSuccess, setOnSuccess] = useState(false);

  useEffect(() => {
    const profile = JSON.parse(localStorage.profile);
    setCompanyId(profile.company_id);

    if (isCompanyUser(profile.role)) {
      navigate("/quotes");
    }
  }, []);

  useEffect(() => {
    setData(handleQuoteItems());
  }, []);

  const handleQuoteItems = () => {
    return loadQuoteItems().map((item) => {
      return {
        ...item,
        type: _.capitalize(item.type),
        path: item.type === "diamond" ? "/diamonds" : "/jewelry",
      };
    });
  };

  const handleDelete = (e) => {
    const id = parseInt(
      e.currentTarget.parentNode.parentNode.getAttribute("data-id"),
      10,
    );

    const type = _.toLower(
      e.currentTarget.parentNode.parentNode.getAttribute("data-type"),
    );

    if (!id || !type) {
      return;
    }

    const item = findQuoteItem(type, id);

    if (!item) {
      return;
    }

    toggleQuoteItem(type, item);
    setData(handleQuoteItems());
  };

  const handleSubmit = () => {
    const itemIds = data.map((item) => {
      return item.type === "Diamond"
        ? { stone_id: item.id }
        : { jewelry_id: item.id };
    });
    setLoading(true);
    setAlertVisible(false);
    axios
      .post(
        `${process.env.REACT_APP_API_URL}/quotes`,
        {
          quote: {
            company_id: companyId,
            customer_notes: customerNotes,
            item_ids: itemIds,
          },
        },
        {
          headers: {
            Authorization: localStorage.token,
          },
        },
      )
      .then((_response) => {
        clearQuoteItems();
        setAlertVisible(true);
        setAlertMessage("Quote has been submitted");
        setOnSuccess(true);

        setTimeout(() => {
          navigate("/quotes");
        }, 3000);
      })
      .catch((error) => {
        setAlertVisible(true);
        setAlertMessage("Error submitting quote");
        console.error("Error submitting quote:", error);
        setOnSuccess(false);
      });
  };

  //////////////////////////////////////////////////////////////////////////////
  // UI
  //////////////////////////////////////////////////////////////////////////////

  const tableColumns = [
    { key: "data.item_no", label: "Item Number", link: true },
    { key: "type", label: "Type" },
    { key: "data.description", label: "Description" },
    { key: "price", label: "Price" },
    {
      classNames: "text-right",
      content: (
        <button
          onClick={handleDelete}
          title="Remove from quote"
        >
          <MdOutlineRemoveShoppingCart
            className="shrink-0 text-t-red"
            size="16"
          />
        </button>
      ),
      ignoreRowClick: true,
    },
  ];

  const content = (
    <>
      {data.length > 0 ? (
        <>
          {!onSuccess && (
            <Alert
              content={
                "This quote has not been submitted yet. You can add or remove items and submit the request when ready."
              }
              type="warning"
              visible={true}
            />
          )}
          {alertVisible ? (
            <Alert
              content={alertMessage}
              type={onSuccess ? "success" : "error"}
              visible={alertVisible}
            />
          ) : (
            <>
              <ItemGrid
                data={data}
                location="quote"
                removeAllButton
              />
              <textarea
                className="w-full"
                name="customerNotes"
                onChange={(e) => setCustomerNotes(e.target.value)}
                placeholder="Additional notes..."
                rows={3}
                value={customerNotes}
              />
              <button
                className="mb-8 flex w-full items-center justify-center bg-t-red px-2 py-2.5 text-sm font-medium text-white hover:bg-black disabled:cursor-not-allowed disabled:bg-black"
                onClick={(e) => {
                  e.target.disabled = true;
                  handleSubmit();
                }}
              >
                {loading ? (
                  <PiSpinnerLight
                    className="mr-1 shrink-0 animate-spin"
                    size="20"
                  />
                ) : (
                  <MdOutlineForwardToInbox
                    className="mr-1 shrink-0"
                    size="20"
                  />
                )}
                Request Quote
              </button>
            </>
          )}
        </>
      ) : (
        <Alert
          content={
            <span>
              No items have been added to this quote yet. Select some{" "}
              <a
                className="font-bold text-white"
                href="/diamonds"
              >
                diamonds
              </a>{" "}
              or{" "}
              <a
                className="font-bold text-white"
                href="/jewelry"
              >
                jewelry
              </a>{" "}
              items first.
            </span>
          }
          type="error"
          visible={true}
        />
      )}
    </>
  );

  return (
    <>
      <Helmet>
        <title>Quote Cart - Ninacci</title>
        <meta
          name="description"
          content="Review and manage items in your quote cart."
        />
      </Helmet>
      <Header title="Quote Cart" />
      <StandardContainer content={content} />
    </>
  );
}
